import React, { Component } from 'react';
import {
  Button,
  FormGroup,
  Input,
  Label,
  Modal,
  ModalBody,
  ModalFooter,
  ModalHeader
} from 'reactstrap';
import axios from 'axios';
import { get } from 'lodash-es';

class ChargeModal extends Component {
  constructor(props) {
    super(props);
    this.state = {
      chargeData: props.chargeData,
      fetchedStates: false,
      showChargeModal: false,
      states: [],
      validation: {
        taxCountryID: null,
        taxStateID: null,
        taxRate: null,
        taxType: null
      }
    };
  }

  // Use the correct data from props for charge type for the edit permutation
  static getDerivedStateFromProps(props, state) {
    const { showChargeModal } = props;

    if (showChargeModal !== state.showChargeModal) {
      const { chargeData } = props;

      return {
        chargeData,
        showChargeModal,
        validation: {
          taxCountryID: null,
          taxStateID: null,
          taxRate: null,
          taxType: null
        }
      };
    }

    return null;
  }

  async componentDidUpdate() {
    const { showChargeModal } = this.props;
    const { chargeData, fetchedStates } = this.state;
    const taxCountryID = get(chargeData, 'taxCountryID', '');

    // If the user closes the modal, reset fetchedStates to false
    if (!showChargeModal && fetchedStates) {
      // eslint-disable-next-line react/no-did-update-set-state
      this.setState({ fetchedStates: false });
    } else if (showChargeModal && taxCountryID && !fetchedStates) {
      // The user launched the modal in the edit permutation, and has a pre-selected countryCpde
      const states = await this.fetchStates(taxCountryID);
      // eslint-disable-next-line react/no-did-update-set-state
      this.setState({ fetchedStates: true, states });
    }
  }

  fetchStates = async taxCountryID => {
    const stateResponse = await axios(`/app/states?country=${taxCountryID}`);
    const states = get(stateResponse, 'data.data.states', []);
    if (states.length > 0) {
      const allStatesOption = {
        countryCode: taxCountryID,
        stateCode: 'ALL',
        stateName: 'All States / Provinces'
      };
      return [{ ...allStatesOption }, ...states];
    }

    return [];
  };

  // Dynamically set changed input fields in the state
  handleInputChange = e => {
    const { id, value } = e.target;
    const chargeData = { ...this.state.chargeData };
    const validation = { ...this.state.validation };

    chargeData[id] = value;
    delete validation[id];

    this.setState({ chargeData, validation });
  };

  handleSelectCountry = async e => {
    const {
      target: { value }
    } = e;

    const states = await this.fetchStates(value);
    const chargeData = { ...this.state.chargeData };

    chargeData.taxCountryID = value;
    // Reset selected state to empty if the user changes country
    chargeData.taxStateID = '';

    this.setState({ chargeData, states, fetchedStates: true });
  };

  validateTaxRate = e => {
    const {
      target: { id, value }
    } = e;

    const taxRateRegex = /^\d{0,3}(\.\d{1,3})?$/;

    const validation = { ...this.state.validation };
    if (value <= 0 || value > 100) {
      validation[id] = 'Rate % must be a number between 0-100%';
      this.setState({ validation });
    } else if (!taxRateRegex.test(value)) {
      validation[id] = 'Please enter a valid number, up to three decimal places';
      this.setState({ validation });
    }
  };

  validateRequired = e => {
    const {
      target: { id, name, value }
    } = e;
    if (!value) {
      const validation = { ...this.state.validation };
      validation[id] = `${name} is required`;
      this.setState({ validation });
    }
  };

  render() {
    const { showChargeModal } = this.state;

    if (!showChargeModal) {
      return null;
    }

    const { chargeIndex, countries, handleSubmitChargeModal, toggleChargeModal } = this.props;
    const { chargeData, states, validation } = this.state;

    const isEditing = chargeIndex > -1;

    const allFieldsComplete = chargeData.taxType && chargeData.taxRate && chargeData.taxCountryID;
    const stateComplete = states.length === 0 || (states.length > 0 && chargeData.taxStateID);

    // Disable the save button if there are validation errors or missing fields
    const isModalDisabled = !allFieldsComplete
      || !stateComplete
      || Object.keys(validation).some(key => validation[key]);

    return (
      <Modal
        centered
        className="form-modal"
        isOpen={showChargeModal}
        toggle={() => toggleChargeModal()}
      >
        <ModalHeader toggle={() => toggleChargeModal()} tag="h1">
          {`${isEditing ? 'Edit' : 'Add'} Charge Type`}
        </ModalHeader>
        <ModalBody>
          <FormGroup>
            <Label className="font-weight-bold" for="taxType">
              Tax Type
            </Label>
            <Input
              id="taxType"
              className={`${get(validation, 'taxType') ? 'is-invalid' : ''}`}
              maxLength="150"
              name="Tax type"
              onBlur={e => this.validateRequired(e)}
              onChange={e => this.handleInputChange(e)}
              value={get(chargeData, 'taxType', '')}
            />
            {get(validation, 'taxType') && (
              <p className="color-danger my-0">{validation.taxType}</p>
            )}
          </FormGroup>

          <FormGroup>
            <Label className="font-weight-bold" for="taxRate">
              Rate %
            </Label>
            <Input
              id="taxRate"
              className={`${get(validation, 'taxRate') ? 'is-invalid' : ''}`}
              name="Rate %"
              onBlur={e => {
                this.validateTaxRate(e);
                this.validateRequired(e);
              }}
              onChange={e => this.handleInputChange(e)}
              value={get(chargeData, 'taxRate', '')}
            />
            {get(validation, 'taxRate') && (
              <p className="color-danger my-0">{validation.taxRate}</p>
            )}
          </FormGroup>

          <FormGroup>
            <Label className="font-weight-bold" for="taxCountryID">
              Country / Region
            </Label>
            <Input
              type="select"
              id="taxCountryID"
              name="Country / Region"
              onChange={e => this.handleSelectCountry(e)}
              value={get(chargeData, 'taxCountryID', '')}
            >
              <option value="">Select Country</option>
              {countries
                && countries.map(country => (
                  <option key={country.countryCode} value={country.countryCode}>
                    {country.countryName}
                  </option>
                ))}
            </Input>
          </FormGroup>

          {states.length > 0 && (
            <FormGroup>
              <Label className="font-weight-bold" for="taxStateID">
                State / Province
              </Label>
              <Input
                type="select"
                id="taxStateID"
                name="State / Province"
                onChange={e => this.handleInputChange(e)}
                value={get(chargeData, 'taxStateID', '')}
              >
                <option value="">Select State / Province</option>
                {states
                  && states.map(state => (
                    <option key={state.stateCode} value={state.stateCode}>
                      {state.stateName}
                    </option>
                  ))}
              </Input>
            </FormGroup>
          )}
        </ModalBody>

        <ModalFooter>
          <Button
            className={isModalDisabled ? 'mr-3 cursor-not-allowed' : 'mr-3'}
            color="primary"
            disabled={isModalDisabled}
            onClick={() => handleSubmitChargeModal(chargeData, chargeIndex)}
          >
            {isEditing ? 'Save Changes' : 'Add Charge Type'}
          </Button>
          <Button color="link" onClick={() => toggleChargeModal()}>
            Cancel
          </Button>
        </ModalFooter>
      </Modal>
    );
  }
}

export default ChargeModal;
